import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Popover } from '@material-ui/core';
import { MdDelete, MdEdit, MdAdd } from 'react-icons/md';
import { FaMoneyBillWave } from 'react-icons/fa';

import Paper from 'components/Paper';
import { LoadingPage } from 'components/Loading';
import SearchInput from 'components/SearchInput';
import Table from 'components/Table';
import Button from 'components/Button';
import { Row } from 'styles/components';
import AccountsReceivableActions from 'store/ducks/accountsReceivable';
import Modal from 'components/Modal';

import {
  Container,
  WrapperTable,
  WrapperAdd,
  AddIcon,
  DeleteIcon,
  EditIcon,
  BaixarIcon,
  EstornarIcon,
  WrapperPopover,
  PopoverText,
  PopoverButton,
  StatusBadge,
} from './styles';

function ContasReceber() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { accounts, isLoading } = useSelector(
    (state) => state.accountsReceivable
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [openBaixaModal, setOpenBaixaModal] = useState(false);
  const [baixaMode, setBaixaMode] = useState(null); // 'baixar' | 'estornar'

  const data = accounts.map((item) => {
    let dueDateFormatted = '-';
    let receiptDateFormatted = '-';
    
    try {
      if (item.dueDate) {
        const dueDate = item.dueDate instanceof Date 
          ? item.dueDate 
          : new Date(item.dueDate);
        if (!isNaN(dueDate.getTime())) {
          dueDateFormatted = format(dueDate, 'dd/MM/yyyy');
        }
      }
      if (item.receiptDate) {
        const receiptDate = item.receiptDate instanceof Date 
          ? item.receiptDate 
          : new Date(item.receiptDate);
        if (!isNaN(receiptDate.getTime())) {
          receiptDateFormatted = format(receiptDate, 'dd/MM/yyyy');
        }
      }
    } catch (error) {
      console.error('Erro ao formatar data:', error);
    }

    return {
      ...item,
      actions: item.id,
      id: item.id,
      description: item.description,
      value: Number(item.value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
      dueDate: dueDateFormatted,
      receiptDate: receiptDateFormatted,
      status: item.status,
      customer: item.customer || '-',
      category: item.category || '-',
    };
  });

  const filteredData = data.filter(
    (item) =>
      (item.description || '').toUpperCase().includes(search.toUpperCase()) ||
      (item.customer || '').toUpperCase().includes(search.toUpperCase()) ||
      (item.category || '').toUpperCase().includes(search.toUpperCase())
  );

  useEffect(() => {
    dispatch(AccountsReceivableActions.getAccountsReceivableRequest());
  }, [dispatch]);

  if (isLoading) {
    return <LoadingPage />;
  }

  const handleDelete = () => {
    if (selectedId) {
      dispatch(AccountsReceivableActions.removeAccountReceivableRequest(selectedId));
      setOpen(false);
      setAnchorEl(null);
    }
  };

  const handleEdit = (id) => {
    history.push(`/financeiro/contas-receber/edit/${id}`);
  };

  const openConfirmBaixa = (id, mode) => {
    setSelectedId(id);
    setBaixaMode(mode);
    setOpenBaixaModal(true);
  };

  const handleConfirmBaixa = () => {
    const conta = accounts.find((a) => String(a.id) === String(selectedId));
    if (!conta) {
      setOpenBaixaModal(false);
      setBaixaMode(null);
      setSelectedId(null);
      return;
    }

    if (baixaMode === 'baixar') {
      const hoje = new Date().toISOString().split('T')[0];
      dispatch(
        AccountsReceivableActions.updateAccountReceivableRequest(
          {
            ...conta,
            status: 'received',
            receiptDate: hoje,
          },
          null
        )
      );
    } else if (baixaMode === 'estornar') {
      dispatch(
        AccountsReceivableActions.updateAccountReceivableRequest(
          {
            ...conta,
            status: 'pending',
            receiptDate: null,
          },
          null
        )
      );
    }

    setOpenBaixaModal(false);
    setBaixaMode(null);
    setSelectedId(null);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', color: '#F27160' },
      received: { label: 'Recebida', color: '#44DD30' },
      overdue: { label: 'Vencida', color: '#F27160' },
    };
    const statusInfo = statusMap[status] || statusMap.pending;
    return (
      <StatusBadge color={statusInfo.color}>{statusInfo.label}</StatusBadge>
    );
  };

  return (
    <Container>
      <Paper
        title="CONTAS A RECEBER"
        icon={<FaMoneyBillWave size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          <Row>
            <SearchInput
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              value={search}
            />
            <WrapperAdd>
              <div
                onClick={() => history.push('/financeiro/contas-receber/new')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: '36px',
                  minWidth: '120px',
                  padding: '0 20px',
                  backgroundColor: '#FFF28D',
                  boxShadow: '0px 3px 6px #00000029',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#122d58',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <MdAdd size={20} style={{ marginRight: '10px', color: '#122d58' }} />
                Nova Conta
              </div>
            </WrapperAdd>
          </Row>
        }
      >
        <WrapperTable>
          <Table
            columns={[
              { label: 'Ações', id: 'actions' },
              { label: 'Descrição', id: 'description' },
              { label: 'Cliente', id: 'customer' },
              { label: 'Categoria', id: 'category' },
              { label: 'Valor', id: 'value' },
              { label: 'Vencimento', id: 'dueDate' },
              { label: 'Recebimento', id: 'receiptDate' },
              { label: 'Status', id: 'status' },
            ]}
            activePage={0}
            totalPages={Math.ceil(filteredData.length / 10)}
            data={filteredData}
            customComponent={{
              actions: (id) => (
                <Row>
                  {(() => {
                    const conta = accounts.find((a) => String(a.id) === String(id));
                    if (!conta) return null;
                    if (conta.status === 'received') {
                      return (
                        <EstornarIcon
                          title="Estornar baixa"
                          onClick={() => openConfirmBaixa(id, 'estornar')}
                          style={{ marginRight: '10px' }}
                        />
                      );
                    }
                    return (
                      <BaixarIcon
                        title="Baixar título"
                        onClick={() => openConfirmBaixa(id, 'baixar')}
                        style={{ marginRight: '10px' }}
                      />
                    );
                  })()}
                  <EditIcon
                    onClick={() => handleEdit(id)}
                    style={{ marginRight: '10px' }}
                  />
                  <DeleteIcon
                    onClick={(e) => {
                      setSelectedId(id);
                      setAnchorEl(e.currentTarget);
                      setOpen(true);
                    }}
                  />
                </Row>
              ),
              status: (status) => getStatusBadge(status),
            }}
          />
        </WrapperTable>
      </Paper>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false);
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <WrapperPopover>
          <PopoverText>Tem certeza que deseja excluir?</PopoverText>
          <Row>
            <PopoverButton onClick={handleDelete}>Sim</PopoverButton>
            <PopoverButton
              onClick={() => {
                setOpen(false);
                setAnchorEl(null);
              }}
            >
              Não
            </PopoverButton>
          </Row>
        </WrapperPopover>
      </Popover>

      <Modal
        open={openBaixaModal}
        onClose={() => {
          setOpenBaixaModal(false);
          setBaixaMode(null);
          setSelectedId(null);
        }}
        title={baixaMode === 'estornar' ? 'Confirmar estorno' : 'Confirmar baixa'}
      >
        <div style={{ padding: '20px', minWidth: '400px' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            {baixaMode === 'estornar'
              ? 'Tem certeza que deseja estornar a baixa deste título?'
              : 'Tem certeza que deseja baixar este título?'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button
              label="Cancelar"
              onClick={() => {
                setOpenBaixaModal(false);
                setBaixaMode(null);
                setSelectedId(null);
              }}
              color="secondary"
            />
            <Button
              label={baixaMode === 'estornar' ? 'Estornar' : 'Baixar'}
              onClick={handleConfirmBaixa}
              color="primary"
            />
          </div>
        </div>
      </Modal>
    </Container>
  );
}

export default ContasReceber;

